import { env } from '@/env';
import { getQgammonToken, hasStoredSession } from '@/lib/logto';

/**
 * Centralised QGammon gameserver auth.
 *
 * THE BUG THIS FIXES: every TMA dev build authenticated to the gameserver as
 * the SAME `X-Dev-Bypass` user ("Dev") — handshake `auth.devBypass` and REST
 * `X-Dev-User` both came from static env. Two real humans queuing ranked
 * collided on one userId (second enqueue → 409 already_queued) and could never
 * pair. Matchmaking/invites/matches need PER-USER identity, which only a real
 * per-user Logto token provides (the gameserver provisions a row by JWT `sub`).
 *
 * RESOLUTION ORDER (shared by REST and the Socket.IO handshake):
 *   1. A real Logto access token scoped to `https://qgammon.qlash.gg/api`
 *      (getQgammonToken) — the production / signed-in path, per-user identity.
 *   2. The dev-bypass env (`env.qgammonDevBypass` + optional `qgammonDevUser`)
 *      — dev-only escape hatch, gated to dev in env.ts; never set in prod.
 *      ONLY used when there is NO Logto session (`!hasStoredSession()`): the
 *      anonymous-dev case (`?dev=1`, no login). If the user IS signed in but the
 *      qgammon token mint failed, we deliberately do NOT fall back — masking a
 *      real signed-in user as the shared "Dev" identity is exactly the bug that
 *      hides dead-grant / missing-resource-grant failures. We return no auth so
 *      the failure surfaces (and a dead grant has already forced re-login).
 *   3. Neither → no auth material attached. The gameserver answers 401, which
 *      the caller surfaces as an error.
 *
 * Both helpers are async because step 1 mints/refreshes the token on demand
 * (through the shared serialized refresh-token redemption in logto.ts). The
 * hooks and the RTK Query client share THIS implementation so the
 * header/handshake construction lives in exactly one place.
 */

/**
 * Headers for a REST call to the gameserver (:4001) or qgammon-api (:4000):
 * `Authorization: Bearer <logto>` when a token is available, else the
 * `X-Dev-Bypass` (+ optional `X-Dev-User`) dev headers, else nothing.
 *
 * Pass `json: true` to also set `Content-Type: application/json` for a body.
 */
export async function qgammonAuthHeaders(json = false): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  if (json) headers['Content-Type'] = 'application/json';

  // 1. Real per-user Logto token (production / signed-in path).
  const token = await getQgammonToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  // 2. Dev-bypass fallback — ONLY when there's no real session (anonymous dev).
  // A signed-in user whose token mint failed must NOT be masked as shared "Dev".
  if (env.qgammonDevBypass && !hasStoredSession()) {
    headers['X-Dev-Bypass'] = env.qgammonDevBypass;
    if (env.qgammonDevUser) headers['X-Dev-User'] = env.qgammonDevUser;
  }

  // 3. Neither: return as-is (the server will answer 401).
  return headers;
}

/**
 * The Socket.IO handshake `auth` object for the gameserver: `{ accessToken }`
 * when a Logto token is available, else `{ devBypass, devUser? }`, else `{}`.
 * Mirrors qgammonAuthHeaders' resolution order for the WS path (the gameserver
 * reads `auth.accessToken` first, then `auth.devBypass`/`auth.devUser`).
 */
export async function qgammonSocketAuth(): Promise<Record<string, string>> {
  // 1. Real per-user Logto token (production / signed-in path).
  const token = await getQgammonToken();
  if (token) {
    return { accessToken: token };
  }

  // 2. Dev-bypass fallback — ONLY when there's no real session (anonymous dev).
  // A signed-in user whose token mint failed must NOT be masked as shared "Dev".
  const auth: Record<string, string> = {};
  if (env.qgammonDevBypass && !hasStoredSession()) {
    auth.devBypass = env.qgammonDevBypass;
    if (env.qgammonDevUser) auth.devUser = env.qgammonDevUser;
  }

  // 3. Neither: empty handshake (the server will reject the connection).
  return auth;
}
